// Client-side view of the game: the last Hello (world + rule catalogs), the
// last Empire frame (the player's private state) and the current selection.
// Everything here is a cache of server truth — the client never simulates.

import { noteChatLine } from './ui/chatfilter';
import type {
  BuildingSpec, Captain, CasinoLobby, CasinoResult, CasinoTable, Catalog,
  ChatMsg, ClanInfo, CoursePlot, Empire, Fairway, FairwayBuoy, DevBuild, InboxMsg, MapMarker, Market, Module, Monument,
  Wreck, QuakeInfo, Hello, Job, ModuleSpec, Resources, Scoreboard, SeasonReport,
  SeasonSummary, SettlementClock, State,
  ReturnBriefing,
  ShipSpec, SiegeInfo, Site, TechSpec, TorpedoSpec, WarState, ProductionState, BuildingPreview, InboxLedgerState,
  SeaObjectKind, SeaObjectSpec, AdminCache, CargoSighting,
} from './gen/proto/aquaphobia';
import {
  Building, CatalogRules, ChatChannel, HeatmapKind, JobKind, type ModuleFamily, ShipClass,
} from './gen/proto/aquaphobia';
import {
  HEAT_ALPHA_LO, HEAT_ALPHA_HI, HEAT_HUES, HEAT_RAMPS,
} from './gen/presentation';
import { RESOURCES } from './gen/balance';
import type { FeatureFlag } from './gen/featureflags';
// The casino's own slice and its deadline anchor (casino.md). The type and
// the sums are a pure module, so the panels and their tests share them.
import { type CasinoSlice, foldCasinoResult, foldCasinoTable, newCasinoSlice } from './ui/casino/casinostate';
import { CHAT_ENTRY_LIMIT, repeatsLine, type ClanChatEntry, type ClanChatMessage, type ClanChatStop } from './net/clanchat';
import type { Timelapse } from './net/timelapse';
import { state as S } from './gen/strings';
import { ControlGroups } from './groups';
import { ScanSeen } from './ui/cards/scanage';
import { GuardClocks } from './guards';
import { LevelPreviews } from './ui/overlay/levelcache';
import { LocalReads } from './ui/inbox/localread';
import { resIcon } from './ui/resicon';
import { holdRoundOrder } from './ui/roundorder';
import { setBanners } from './world';
import { bandField, currentField, fieldChanged, tideClock, tideElapsedSec, tideFactorAt, type TideClock } from './deep';
import { SurveyStore, clearUnsurveyed, mergeSurveyed, type PoiLiveSeen, type SurveyDelta } from './survey';
import { SeamountStore } from './seamounts';
import { SeaObjectStore, setSeaFigures } from './seaobjects';
import { setPoiRules } from './render/poi-rules';
import { spanText } from './locale/span';
import { secOfDay } from './locale/clock';

/**
 * What `state.rules` answers before the first Hello lands (R355). Every
 * figure reads 0, which no sentence that uses one is on screen long
 * enough to show: they are all empty-state lines of a panel that needs a
 * catalog to draw anything at all.
 */
const RULES_UNTIL_HELLO: CatalogRules = CatalogRules.fromPartial({});

/**
 * The global clan chat, as this client holds it (R222, R225).
 *
 * The CLAN tab of the chat does not read `chat`: its room is on the
 * meta server, and a second socket carries it (`net/clanchat.ts`). The game
 * socket refuses every clan line, so the two transcripts never mix.
 *
 * The slice is empty and disconnected in direct mode, where no socket is
 * built at all: a `?server=` lane has no meta server to dial.
 */
export interface ClanChatSlice {
  /** The transcript, oldest first, capped at CHAT_ENTRY_LIMIT. */
  entries: ClanChatEntry[];
  /** The clan the transcript belongs to, or 0 before the welcome. */
  clanId: number;
  connected: boolean;
  /** Why the socket gave up for good, or null while it still tries. */
  stop: ClanChatStop | null;
  /** This account's public id, so the view can mark its own lines. */
  selfId: string;
  /** The chat relay of the clan copies the transcript to Discord
   *  (player-reach.md §3.10, R1641). */
  relay: boolean;
}

/**
 * The channel the chat opens on, and the channel it falls back to.
 *
 * Faction chat is the one that always has traffic, so it is where a new
 * commander lands rather than staring at an empty world channel. It is also
 * where the box lands when the channel it was on goes away — a commander
 * who leaves a clan loses the CLAN tab, and must not sit on a dead room.
 */
export const DEFAULT_CHANNEL: ChatChannel = ChatChannel.CHAT_CHANNEL_FACTION;

/** The newest live chat line that counted as unread (R1799): a line of the
 *  game socket or of the clan socket, and the `performance.now()` of its
 *  arrival. */
export type FreshLine =
  | { kind: 'game'; line: ChatMsg; atMs: number }
  | { kind: 'clan'; entry: ClanChatMessage; atMs: number };

/** What the context slot shows. A point is a point of interest of the
 *  survey (R2626, R3138): its card stands in the slot as a site card does. */
export type Selection =
  | { kind: 'fleet'; id: number }
  | { kind: 'site'; id: number }
  | { kind: 'point'; id: number }
  | null;

export const ZERO_RES: Resources = {
  alloy: 0, biomass: 0, volatiles: 0, isotopes: 0, credits: 0,
};

/** One map heatmap mode: what the field means, what colours it and what the
 * legend writes at each end of the ramp. The list is the whole mode set and
 * its order is the hotkey order — the first claim of the `heat` binding is
 * index 0. The mode names no key of its own (R853): a caller that prints one
 * reads `claimLabel(KEYS.map.heat.claims[i])`. */
export interface HeatMode {
  kind: HeatmapKind;
  /** The short name on the button, the legend and the notice. */
  label: string;
  /** One line of what the field shows. */
  what: string;
  /** The ramp hue, as a packed 0xRRGGBB number. */
  color: number;
  /**
   * The dark end of the ramp, as a packed 0xRRGGBB number (R258). A mode that
   * states one paints a dark shade of its hue at the faintest bucket and
   * eases to the full hue at the strongest, so the ramp separates on
   * lightness as well as on alpha. A mode that states none paints one hue
   * at every bucket, which is what every mode did before.
   */
  dark?: number;
  /**
   * The faintest bucket's alpha, when the shared `HEAT_ALPHA_LO` is wrong
   * for this mode (R259). A dark endpoint composites to almost nothing at
   * 0.12, so a mode that states one must pay for its faintest bucket with
   * alpha. A mode that states none uses the shared constant.
   */
  alphaLo?: number;
  /**
   * The strongest bucket's alpha, when the shared `HEAT_ALPHA_HI` is wrong
   * for this mode (R259). The bottom of a ramp is pinned by the mark the
   * faintest bucket must carry and the top by this alpha, so the room the
   * middle of the ramp has is the distance between them. A mode with a dark
   * endpoint and a numbered legend needs more of it than 0.12 → 0.68 gives.
   */
  alphaHi?: number;
  /** The legend's two end labels. */
  lo: string;
  hi: string;
  /**
   * The feature flag the mode stands under (R2870). The VIEW heat page,
   * the KEYS fold and the chord leave the mode out while the world did not
   * latch it. The flag names the VIEW group of chart pieces the mode
   * shows, too: the pick of the mode shows that group while the VIEW pill
   * hides it.
   */
  flag?: 'currents' | 'depth';
  /**
   * The client draws the mode from the fields of the hello (R2870). The
   * server computes no field for it, so the client asks the server for
   * NONE, and the heat layer, the minimap and the heat peek show nothing.
   */
  local?: true;
}

/**
 * The ramp alphas, low bucket to high bucket. The hue is one hue across a
 * mode's ramp and the alpha climbs, so the field reads as one quantity and
 * the map keeps showing through it. A mode that states a `dark` endpoint
 * climbs in lightness as well, from a dark shade of that same hue (R258).
 *
 * The span was chosen against the water background (#020b12): at 0.68 every
 * hue below composites into the dark lightness band (OKLCH L 0.48-0.67) and
 * clears 3:1 against the water, so the top of a ramp is a real mark. At 0.12
 * the bottom is a hint, which is what an almost-empty cell must be.
 *
 * These two are the default span. A mode states `alphaLo` or `alphaHi` to
 * take a span of its own (R259); SOV and ACTIVITY · SEASON do (R2289).
 * The pair lives in data/presentation/presentation.toml (R312).
 */
export { HEAT_ALPHA_LO, HEAT_ALPHA_HI };

/**
 * Every mode, in the claim order of the `heat` binding (R853). Only one mode
 * is ever on the map, and the legend names it in text, so the hues do not
 * have to separate from each other under colour blindness — they have to
 * separate from the dark water, which each of them does.
 */
export const HEAT_MODES: HeatMode[] = [
  {
    kind: HeatmapKind.HEATMAP_KIND_NONE,
    label: S.heat_off_label, what: S.heat_off_what, color: HEAT_HUES.off, lo: '', hi: '',
  },
  {
    // SOV is the one mode with a numbered legend, so a commander reads a
    // value off it and not just a shape. Twelve alpha steps of one hue put
    // 2, 3 and 4 within 0.035 of OKLab lightness of each other over the
    // dark water, and the middle of the field read as one wash. The dark
    // endpoint spreads the same twelve buckets over a wider band (R258),
    // and the mode's own alpha span pays for the faintest bucket and buys
    // the middle its room (R259). The four numbers are one ramp: the
    // measured table is in R259.
    kind: HeatmapKind.HEATMAP_KIND_SOV,
    label: S.heat_sov_label, what: S.heat_sov_what,
    color: HEAT_HUES.sov, ...HEAT_RAMPS.sov,
    lo: S.heat_sov_lo, hi: S.heat_sov_hi,
  },
  {
    kind: HeatmapKind.HEATMAP_KIND_ACTIVITY,
    label: S.heat_activity_label, what: S.heat_activity_what,
    color: HEAT_HUES.activity, lo: S.heat_activity_lo, hi: S.heat_activity_hi,
  },
  {
    // A season of credits covers most of the map, and one hue at every
    // bucket read as one yellow sheet. The dark endpoint and the wide span
    // keep a quiet cell near the water and let the worked lanes stand out
    // (R2289; the ramp fact is in numbers.md §21).
    kind: HeatmapKind.HEATMAP_KIND_ACTIVITY_SEASON,
    label: S.heat_activity_season_label, what: S.heat_activity_season_what,
    color: HEAT_HUES.activity_season, ...HEAT_RAMPS.activity_season,
    lo: S.heat_activity_season_lo, hi: S.heat_activity_season_hi,
  },
  {
    kind: HeatmapKind.HEATMAP_KIND_KILLS,
    label: S.heat_kills_label, what: S.heat_kills_what,
    color: HEAT_HUES.kills, lo: S.heat_kills_lo, hi: S.heat_kills_hi,
  },
  {
    kind: HeatmapKind.HEATMAP_KIND_KILLS_SEASON,
    label: S.heat_kills_season_label, what: S.heat_kills_season_what,
    color: HEAT_HUES.kills, lo: S.heat_kills_season_lo, hi: S.heat_kills_season_hi,
  },
  {
    // The client draws it (R2870): the current heat look of the World
    // theme, with no depth tints under it.
    kind: HeatmapKind.HEATMAP_KIND_CURRENTS,
    label: S.heat_currents_label, what: S.heat_currents_what,
    color: HEAT_HUES.currents, lo: S.heat_currents_lo, hi: S.heat_currents_hi,
    flag: 'currents', local: true,
  },
  {
    kind: HeatmapKind.HEATMAP_KIND_BEACON,
    label: S.heat_beacon_label, what: S.heat_beacon_what,
    color: HEAT_HUES.beacon, lo: S.heat_beacon_lo, hi: S.heat_beacon_hi,
  },
  {
    // The client draws it (R2870): the depth tints in the depth heat look,
    // with no streaks and no speed field over them.
    kind: HeatmapKind.HEATMAP_KIND_DEPTH,
    label: S.heat_depth_label, what: S.heat_depth_what,
    color: HEAT_HUES.depth, lo: S.heat_depth_lo, hi: S.heat_depth_hi,
    flag: 'depth', local: true,
  },
  {
    kind: HeatmapKind.HEATMAP_KIND_STABILITY,
    label: S.heat_stability_label, what: S.heat_stability_what,
    color: HEAT_HUES.stability, lo: S.heat_stability_lo, hi: S.heat_stability_hi,
  },
];

/** The mode for a kind, or the OFF row for anything unknown. */
export function heatMode(kind: HeatmapKind): HeatMode {
  return HEAT_MODES.find((m) => m.kind === kind) ?? HEAT_MODES[0];
}

/** Does the player see the mode: it stands under no flag, or the world
 *  latched its flag (R2870)? `has` is `GameState.hasFeatureFlag`. */
export function heatModeShown(m: HeatMode, has: (flag: FeatureFlag) => boolean): boolean {
  return m.flag === undefined || has(m.flag);
}

/** The kind the server is asked for: NONE for a mode the client draws
 *  (R2870), which the server computes no field for. */
export function heatServerKind(kind: HeatmapKind): HeatmapKind {
  return heatMode(kind).local ? HeatmapKind.HEATMAP_KIND_NONE : kind;
}

/**
 * The wall-clock ms a countdown ends at, from the seconds the wire said
 * were left and the clock those seconds run on: a wall second is worth
 * `scale` of them. 0 when nothing is left, which every reader takes as
 * "no countdown" rather than "a countdown at zero".
 */
function deadlineMs(leftSec: number, scale: number): number {
  return leftSec > 0 ? Date.now() + (leftSec / scale) * 1000 : 0;
}

/** WALL seconds until the wall-clock ms `endsMs`, or 0 for no deadline. */
function wallLeftSec(endsMs: number): number {
  return endsMs > 0 ? Math.max(0, (endsMs - Date.now()) / 1000) : 0;
}

export class GameState {
  hello: Hello | null = null;
  empire: Empire | null = null;
  sites = new Map<number, Site>();
  selection: Selection = null;
  /**
   * The five fleet control groups of this empire (R539).
   *
   * The server is the store: `setEmpire` takes the taken slots from the
   * frame whole, and a press writes here first only so the badge and the
   * buttons answer at once. The model is pure and the same one the arcade
   * match holds.
   */
  readonly groups = new ControlGroups();
  /** When this client saw each contact's scan reveal open: the age of the
   *  SCANNED chip of a contact's card (round 084 card 05). The feed notes
   *  every State frame. */
  readonly scanSeen = new ScanSeen();
  /** The clocks of the guards this client may read (R2080): an own set
   *  Sentinel with the Refit, an arcade Reef Breakwater of the seat's team
   *  or a public one. The scene notes every State frame. */
  readonly guards = new GuardClocks();
  /** The survey of the viewer's faction and its known points of interest
   *  (v2 §4.1, R2611, R2626). Empty while the wire carries none: every cell
   *  then reads surveyed. The scene notes every State frame. */
  readonly survey = new SurveyStore();
  /** The seamount names of the world (v2 §4.3, R2620, R2633). Empty while
   *  the wire carries none. The scene notes every State frame. */
  readonly seamounts = new SeamountStore();
  /** The sea objects of the picture and the Sapper fields of each own
   *  Sapper (v2 §11.9, R2654, R2657). Empty while the wire carries none.
   *  The scene notes every State frame. */
  readonly seaObjects = new SeaObjectStore();
  /** Every cache of the season while TOGGLE CACHES of the admin panel is
   *  on (R2825), in cache id order. A State frame that carries the list
   *  replaces it; a new hello empties it. */
  adminCaches: readonly AdminCache[] = [];
  /** The answers of the level picker of the build card (R1839). */
  readonly levelPreviews = new LevelPreviews();
  /** The world clock's multiplier against wall time: economy, queue,
   * events, season (R91). Countdowns measured in world seconds convert
   * with this one. */
  timescale = 1;
  /** The map clock's multiplier: movement, combat, reloads, capture
   * cycles. Countdowns measured in tactical seconds convert with this
   * one, and it is usually the larger of the two. */
  tacticalScale = 1;
  /** The calm gate of the shield regen, in map seconds (R1979). The gun-shot
   * layer reads it to tell the opening hit of a fight. 0 before a hello. */
  regenCalmMapSec = 0;
  // The season countdown is anchored in *game* seconds, not wall time
  // (R93): seasonLeftAtSec is what the last wire frame said was left, and
  // seasonAtMs stamps when it said so. The banner shows the world's own
  // clock — a 28-day season reads 28d whatever the timescale is, and a
  // higher timescale only makes it run down faster.
  seasonLeftAtSec = 0;
  seasonAtMs = 0;
  // Wall-clock ms when the founding truce ends (plan §5.2): anchored by the
  // hello, re-anchored by every scoreboard, 0 when weapons are live.
  truceEndsMs = 0;
  // The settlement clock (R2093): the wall-clock ms the settlement ends
  // at (the roll, or the close of a held world) and the ms its drain ends
  // at, from the last SettlementClock (the hello carries one, so a
  // mid-settlement joiner has it at once). Both are 0 outside the
  // settlement. The settlement runs on the wall clock (R2091), so neither
  // takes the timescale.
  settlementEndsMs = 0;
  drainEndsMs = 0;
  settlementHeld = false;
  // Where the next seaquake shock strikes (R803): the last QuakeInfo off
  // the wire, and the wall-clock ms it arrived at. `quakeNextSec` runs its
  // countdown on the world clock the way `seasonLeftSec` runs the season.
  quake: QuakeInfo | null = null;
  quakeAtMs = 0;
  // The maintenance window the meta server plans (deployment.md §6.1), as
  // a WALL unix second, and the second the window is planned to close. It
  // is the one deadline on this object that is not world time: an operator
  // names a wall instant and every world closes at it, so no timescale is
  // applied to it. 0 means NO WINDOW, which is what a fresh client holds
  // and what a cancelled window puts back.
  maintenanceAt = 0;
  maintenanceEndsAt = 0;
  // The operator's line for the player, or the empty string.
  maintenanceNote = '';
  // Wall-clock ms when the base's automated point defence is ready again
  // (R86): anchored by every Empire frame the same way the truce is, so
  // the base tooltip counts down live between frames.
  pdReadyMs = 0;
  // The other half of the base's firing readiness (R88): manual tubes are
  // per-tube, ten game-seconds each. tubeReadyMs is anchored like pdReadyMs
  // and only runs while every tube is cooling.
  tubeReadyMs = 0;
  // The world's time of day, anchored the way the season is: clockAtSec is
  // what the last frame said the clock read, clockAtMs stamps when it said
  // so. Both the hello and every scoreboard anchor it, so a live timescale
  // retune cannot leave the reading running at the old rate. This is the
  // clock a strategic object's vulnerability window is scheduled on, so it
  // is world time and converts with `timescale`.
  clockAtSec = 0;
  clockAtMs = 0;
  // The time of day the season's world clock opened at (R2519). A stamp
  // in world seconds since the start adds it to read as a time of day.
  // A roll moves it, so the board carries it too.
  clockOriginSec = 12 * 3600;
  // Wall-clock stamp of the last Empire frame, for the build queue. A job
  // carries its ETA in world game-seconds and the server sends an Empire
  // frame only when the empire *changes* — a job that merely drains does
  // not — so the queue has to count between frames or it reads frozen.
  queueAtMs = 0;
  clan: ClanInfo | null = null;
  market: Market | null = null;
  // The casino (casino.md): the last lobby, the last table and the last
  // settled hand, plus the `performance.now()` stamp of the table frame.
  // Every countdown the table states is anchored on that stamp, because a
  // frame states the seconds it had when it left the server.
  casino: CasinoSlice = newCasinoSlice();
  chat: ChatMsg[] = [];
  // How many lines `addChat` has dropped off the front at the cap. A chat
  // key is `chatDropped` plus the index, so one key names one line for as
  // long as the line is held (R1338).
  chatDropped = 0;
  channel: ChatChannel = DEFAULT_CHANNEL;
  unread = new Map<ChatChannel, number>();
  // Is the chat open on the screen: the chat band of a telephone, or the
  // chat pane of a desktop over its line stop (R1747, R1799)? A line on the
  // channel the player reads is read only while the chat shows it, so a
  // shut chat counts that line too, and the VIEW button and the chat line
  // show the count. The overlay writes it through `setChatBoxOpen`.
  chatBoxOpen = true;
  // The newest live line that counted as unread, with the moment it came
  // (R1799). The chat line of the dock shows it for `chat_line_sec`, then
  // the count. The overlay reads it and decides what shows; a line of a
  // blocked sender never shows.
  chatFresh: FreshLine | null = null;
  // The CLAN tab's transcript, from the meta server's socket (R225). It is
  // a separate slice because it is a separate room: the game socket carries
  // no clan line, and this socket carries nothing else.
  clanChat: ClanChatSlice = {
    entries: [], clanId: 0, connected: false, stop: null, selfId: '', relay: false,
  };
  sieges: SiegeInfo[] = [];
  board: Scoreboard | null = null;
  season: SeasonReport | null = null;
  // The season summary (R1518, ui-map-interface.md §3.4): the last
  // SeasonSummary off the wire, or null outside the lock of the scores.
  // summaryAtMs stamps its arrival, because its countdown is a span from
  // that moment. summaryShown says the card stands on the glass, and
  // summaryClosed names the world and season whose card the player put
  // away, so a reconnect or a hold change does not open it again.
  summary: SeasonSummary | null = null;
  // The return briefing (ui/briefing.ts, R1605): the sums of the last
  // absence of this commander, as the hub sent them after the first
  // Empire frame of the return. It stays for the page's life, so a
  // reconnect keeps the block at the head of the INBOX; the next return
  // replaces it.
  briefing: ReturnBriefing | null = null;
  summaryAtMs = 0;
  summaryShown = false;
  private summaryClosed = '';
  // WAR-dock data (plan §4, R83), fetched on demand when the tab opens.
  // warAtMs stamps the fetch so ages can keep ticking between fetches.
  war: WarState | null = null;
  warAtMs = 0;
  // ECONOMY · PRODUCTION's income ring (R1489), fetched on demand when
  // the pill opens. It is the empire's own and nobody else's.
  production: ProductionState | null = null;
  // The season timelapse of the running season (chronicle.md §10.3,
  // R1595), fetched on demand and decoded. The owners are public, so the
  // frames are the same for every commander. A hello is a new picture and
  // drops it.
  timelapse: Timelapse | null = null;
  // The inbox ledger of this seat (inbox.md §5.6, R1831): the hour rows,
  // the day rows and the departure, as the last answer to a ledger query
  // sent them. SINCE YOU LEFT and the DIGEST sum it (§7, R1851). The
  // client asks on an event, so the answer stands until the next ask.
  inboxLedger: InboxLedgerState | null = null;
  // Dev build watch (R89), or null — which is what every server that is not
  // a `-watch-git` dev box leaves it as, so the top bar's dev badge cannot
  // appear in a real deployment.
  dev: DevBuild | null = null;
  // The inbox (v12), newest first — a cache of the last Inbox frame, which
  // the server sends only in answer to a fetch. The unread badge does not
  // read this: it reads empire.inboxUnread, which every Empire frame
  // carries, so the badge is live before the list was ever fetched.
  inbox: InboxMsg[] = [];
  // Has any Inbox frame arrived? Before the first one the record shows
  // that it waits, and no count and no MARK ALL READ (audit P7).
  inboxFetched = false;
  // The reads the server has not counted yet (inbox.md §6, audit P5).
  readonly inboxReads = new LocalReads();
  // Every wreck of the season (R801): the hello replaces the list and a
  // Wrecks frame appends to it. A wreck is public, so this is the same
  // list for every client of the world. The chart furniture draws it
  // (card 08), and its controller finds a change by one compare a frame.
  wrecks: Wreck[] = [];
  // The cargo sightings of this commander (v2 §9.6, R2843), by the fleet
  // id of the wreck: what his sonar last heard of a wreck with cargo. The
  // public wreck names no cargo, so a wreck with no sighting reads as a
  // plain wreck. The State frame carries the rows as deltas. A row with
  // `recovered` is the mark of a wreck he knows is recovered (R2850): it
  // has no take, and no reader takes it as cargo (`wreckTakes`).
  cargoSightings = new Map<number, CargoSighting>();
  // Moves when a wreck gains or loses its sighting or its row turns
  // recovered, and not when a later hearing writes a known row again
  // (R2847, R2850). The wreck piece draws the cargo bale of a sighted
  // wreck, and its `outgrown` compares this figure once a frame.
  cargoSightingsRev = 0;
  // The monuments of this map (R964): the memorial reef of the winner of
  // the last season, up to three hold sites of earlier winners, and the
  // pool monuments of winners of other worlds (R1243). They
  // are derived from the world seed and the winner list, so the hello
  // carries the whole set and no later frame changes it. The chart
  // furniture draws them (cards 63 and 64).
  monuments: Monument[] = [];
  // The map markers this client may see (player-reach.md §2, R1292): the
  // markers of its squad and of its faction, oldest first. Every Markers
  // frame REPLACES the list, so the chart furniture finds a change by one
  // identity compare a frame. A hello empties it, because the hub sends
  // the list of the new world after the join.
  mapMarkers: MapMarker[] = [];
  // The rings of the quick lines (R1607, mockup round 077, card 04): the
  // cell of each quick line with a position that arrived live, and the
  // wall ms it arrived. The map rings each point ONE time; the chart piece
  // drops a ring when its time is up. A backlog line rings nothing.
  quickPings: { cell: number; at: number }[] = [];
  // The map heatmap the player asked for (v13). Only the mode lives here,
  // because the DOM reads it: the field itself is 20 KB of bytes and goes
  // straight from the socket to the render layer.
  //
  // It is deliberately not persisted. A heatmap is an inspection tool, so a
  // new session starts on the plain map.
  heatmap: HeatmapKind = HeatmapKind.HEATMAP_KIND_NONE;
  // How many notices this session has taken off the wire (R548). The chart
  // furniture's imprint prints it as the chart's "corrected through Notice
  // N", which is what a paper chart's margin states. It counts the SESSION
  // and not the world: a chart is corrected by the hand that holds it.
  noticesSeen = 0;

  private listeners: (() => void)[] = [];

  /** Subscribe to any change; returns an unsubscribe function. */
  on(fn: () => void): () => void {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== fn);
    };
  }

  changed() {
    for (const fn of this.listeners) fn();
  }

  /** One more notice arrived off the wire (R548). */
  countNotice() { this.noticesSeen++; }

  setHello(h: Hello) {
    this.hello = h;
    // A query the old socket sent gets no answer on this one (R1839).
    this.levelPreviews.reset();
    // A hello is a new picture of the world. The hub sends the season
    // summary right after it while the scores stand locked (R1518), so a
    // summary of any earlier picture goes: a roll, a reseed, a new world.
    this.summary = null;
    this.summaryShown = false;
    // The banners of a Clan War world rename the factions everywhere
    // (R1260); a hello with none gives the faction names back.
    setBanners(h.banners);
    // The pack figures of the sea objects ride the catalog (R2659); a hello
    // of a world with `sapper` off clears them.
    setSeaFigures(h.catalog);
    // The figures of the point of interest marks ride the catalog too
    // (R2714); a hello without them restores the built figures.
    setPoiRules(h.catalog);
    this.wrecks = h.wrecks.slice();
    // A hello is a new picture; the full State frame after it restates
    // the cargo sightings (R2843).
    if (this.cargoSightings.size > 0) this.cargoSightingsRev++;
    this.cargoSightings = new Map();
    // A new hello is a new map, so the monuments of the old one go (R964).
    this.monuments = h.monuments.slice();
    // A hello is a new world, and the hub sends its markers after it.
    this.mapMarkers = [];
    this.quickPings = [];
    // A read the old socket carried is lost with it (audit P5).
    this.inboxReads.reset();
    // A hello is a new world: the guards of the old one go (R2080).
    this.guards.clear();
    // A hello is a new world: its sea objects come in the first full frame.
    this.seaObjects.reset();
    // A hello is a new connection: TOGGLE CACHES is off on it (R2825).
    this.adminCaches = [];
    // The frames show the map of the hello before it (R1595).
    this.timelapse = null;
    this.timescale = h.timescale || 1;
    this.tacticalScale = h.tacticalScale || this.timescale;
    this.regenCalmMapSec = h.regenCalmMapSec;
    // The hello carries the season as a wall-clock instant, so convert it
    // back to world game-seconds with the world clock's multiplier — the
    // season is world time, never tactical (R91).
    this.seasonLeftAtSec = Math.max(
      0, ((Number(h.seasonEndsUnix) * 1000 - Date.now()) / 1000) * this.timescale,
    );
    this.seasonAtMs = Date.now();
    this.truceEndsMs = deadlineMs(h.truceLeftSec, this.timescale);
    this.clockAtSec = h.clockSec;
    this.clockOriginSec = h.clockOriginSec;
    this.clockAtMs = Date.now();
    // The hello carries the quake (R803), so a joiner in the arc has it at
    // once. It is stored without its own `changed()`: this one follows.
    this.quake = h.quake ?? null;
    this.quakeAtMs = Date.now();
    // The settlement clock rides the hello (R2093); a hello without one is
    // a world outside the settlement, and the old clock goes.
    this.takeSettlement(h.settlement);
    this.sites.clear();
    for (const s of h.sites) this.sites.set(s.id, s);
    // The survey of the hello is a new picture (R2626).
    this.survey.fromHello(h);
    // The seamount names of the hello are every name (R2633).
    this.seamounts.fromHello(h);
    this.changed();
  }

  /** The fairways of the map (v2-the-deep.md §7.1, R2717): the hello
   *  carries them while routes is latched, and no frame changes them. */
  get fairways(): readonly Fairway[] {
    return this.hello?.fairways ?? [];
  }

  /** The buoys of the fairways, under the gate of `fairways`. */
  get buoys(): readonly FairwayBuoy[] {
    return this.hello?.buoys ?? [];
  }

  /** The course plots of the empire, in the order the commander saved
   *  them (v2 §7, R2576, R2589): the Empire frame states the whole list,
   *  so a SAVE, a RENAME and a DELETE all show in the next frame. */
  get plots(): readonly CoursePlot[] {
    return this.empire?.plots ?? [];
  }

  /** The shipping lanes of the faction mates (v2-the-deep.md §7.2 rule 3,
   *  R2646): each with its owner and the name of its commander. The own
   *  lanes are the plots with `lane` set. The server sends them only
   *  while routes is latched. */
  get factionLanes(): readonly CoursePlot[] {
    return this.empire?.lanes ?? [];
  }

  setEmpire(e: Empire) {
    this.empire = e;
    // This frame counts every read an earlier Inbox frame showed (P5).
    this.inboxReads.empireFrame();
    // The server states every taken slot in every frame (R539), so the
    // client replaces its picture rather than merging into it: a press the
    // server refused must not survive the frame that answers it.
    this.groups.replaceAll(e.controlGroups);
    // Both are tube clocks, so both run on the map's time, not the
    // economy's (R91).
    this.pdReadyMs = deadlineMs(e.pointDefenceReadySec, this.tacticalScale);
    this.tubeReadyMs = deadlineMs(e.baseTubeReadySec, this.tacticalScale);
    // The clocks of the arcade guard structures this seat may read
    // (R2085): the Empire frame states the time left, the hello the reach
    // and the period of each one.
    const specs = this.hello?.arcade?.structures ?? [];
    this.guards.observeSites((e.structureGuards ?? []).map((g) => {
      const st = specs.find((x) => x.siteId === g.siteId);
      return { siteId: g.siteId, reach: st?.reachPx ?? 0, every: st?.reloadSec ?? 0, faction: g.owner, leftSec: g.leftSec };
    }), Date.now(), this.tacticalScale);
    // The queue is world time, not map time: it is the economy's clock.
    this.queueAtMs = Date.now();
    this.changed();
  }

  addChat(line: ChatMsg) {
    // The chat filter masks a line once, as it arrives (R2937).
    noteChatLine(line, line.text);
    this.chat.push(line);
    if (this.chat.length > CHAT_ENTRY_LIMIT) {
      this.chat.shift();
      this.chatDropped++;
    }
    // Replayed backlog is not news. The server marks the history it sends a
    // joining client; counting it would make every reload and every
    // reconnect re-raise the unread badges on lines already read.
    if (!line.history && !this.reading(line.channel)) {
      this.countUnread(line.channel);
      this.chatFresh = { kind: 'game', line, atMs: performance.now() };
    }
    this.changed();
  }

  /** Does a new line on `ch` land in front of the player: the chat is
   *  open on that channel (R1747, R1799)? */
  private reading(ch: ChatChannel): boolean {
    return this.chatBoxOpen && ch === this.channel;
  }

  /**
   * The chat opened or shut (R1747, R1799). An open chat shows the channel the
   * player reads, so its count goes. It raises no change: the overlay calls
   * it while it draws, and draws the counts after it.
   */
  setChatBoxOpen(open: boolean) {
    this.chatBoxOpen = open;
    if (open) this.unread.delete(this.channel);
  }

  /** The line of one chat key, or `undefined` when the line has dropped
   *  off the front of the list at the cap (R1338). */
  chatLine(key: number): ChatMsg | undefined {
    return this.chat[key - this.chatDropped];
  }

  /**
   * One line of the global clan chat (R225).
   *
   * It counts unread by the same rule `addChat` uses: a replayed line is
   * not news, and a line on the tab the player is reading is already read.
   * A gap marker never counts, because a marker is a statement about lines
   * that are gone and not a line anybody wrote.
   */
  addClanChat(e: ClanChatEntry) {
    // The replay after a dropped frame can carry a line the live fan-out
    // already delivered.
    if (repeatsLine(this.clanChat.entries, e)) return;
    if (e.kind === 'message') noteChatLine(e, e.body);
    this.clanChat.entries.push(e);
    if (this.clanChat.entries.length > CHAT_ENTRY_LIMIT) {
      this.clanChat.entries.splice(0, this.clanChat.entries.length - CHAT_ENTRY_LIMIT);
    }
    const live = e.kind === 'message' && !e.history;
    if (live && !this.reading(ChatChannel.CHAT_CHANNEL_CLAN)) {
      this.countUnread(ChatChannel.CHAT_CHANNEL_CLAN);
      this.chatFresh = { kind: 'clan', entry: e, atMs: performance.now() };
    }
    this.changed();
  }

  /** One more unread line on a channel the player is not reading. */
  private countUnread(ch: ChatChannel) {
    this.unread.set(ch, (this.unread.get(ch) ?? 0) + 1);
  }

  /** The clan chat socket opened or closed. An open socket clears the stop
   * reason: the socket only opens when the refusal that stopped it is gone. */
  setClanChatConnected(on: boolean) {
    this.clanChat.connected = on;
    if (on) this.clanChat.stop = null;
    this.changed();
  }

  /** The chat relay of the clan turned on or off (R1641). */
  setClanChatRelay(on: boolean) {
    if (this.clanChat.relay === on) return;
    this.clanChat.relay = on;
    this.changed();
  }

  /** The clan chat socket gave up for good. Nothing reconnects afterwards. */
  setClanChatStop(stop: ClanChatStop) {
    this.clanChat.stop = stop;
    this.clanChat.connected = false;
    this.changed();
  }

  /**
   * The welcome frame: the clan this transcript belongs to, and this
   * account's public id. A reconnect to another clan is another
   * transcript, so the entries go; the same clan keeps what is on screen,
   * because the replay carries only what this account has not seen.
   */
  clanChatWelcome(clanId: number, selfId: string) {
    if (clanId !== this.clanChat.clanId) this.clanChat.entries = [];
    this.clanChat.clanId = clanId;
    this.clanChat.selfId = selfId;
    this.clanChat.stop = null;
    this.changed();
  }

  /** Switches channel and clears its unread count. */
  readChannel(ch: ChatChannel) {
    this.channel = ch;
    this.unread.delete(ch);
    this.changed();
  }

  setDev(d: DevBuild) {
    this.dev = d;
    this.changed();
  }

  /**
   * The whole list of the map markers this client may see (R1292). The
   * hub sends it on every change, so the new list replaces the old one.
   */
  setMapMarkers(list: MapMarker[]) {
    this.mapMarkers = list.slice();
  }

  /**
   * A quick line with a position arrived (R1607): the map rings the point
   * once. The list replaces itself, so the chart piece finds a change by
   * one identity compare, as it does for the markers.
   */
  addQuickPing(cell: number, at: number = Date.now()) {
    this.quickPings = [...this.quickPings, { cell, at }];
  }

  /** Drops the rings whose time is up, at a wall ms (R1607). */
  pruneQuickPings(now: number, lifeMs: number) {
    if (this.quickPings.length === 0) return;
    const live = this.quickPings.filter((p) => now - p.at < lifeMs);
    if (live.length !== this.quickPings.length) this.quickPings = live;
  }

  /** The wrecks one step added (R801). The list only grows until the next
   *  hello. The public wreck never changes after it sank (R2843), so no
   *  frame restates one. */
  addWrecks(list: Wreck[]) {
    if (list.length === 0) return;
    this.wrecks.push(...list);
    // The hello carries only the wrecks of the last `wreckWindowSec`
    // (R804), so a client that stays connected drops the older ones too:
    // both show the same sea bed, and a long session holds no season of
    // wrecks. The list is in the order the hulls sank, so the old ones
    // are a run at the front.
    const cut = this.seasonElapsedSec() - this.rules.wreckWindowSec;
    if (this.rules.wreckWindowSec <= 0 || cut <= 0) return;
    const keep = this.wrecks.findIndex((w) => w.sunkSec >= cut);
    if (keep !== 0) this.wrecks = keep < 0 ? [] : this.wrecks.slice(keep);
  }

  /** World seconds since the season began, on the season clock. */
  seasonElapsedSec(): number {
    return Math.max(0, this.rules.seasonSec - this.seasonLeftSec());
  }

  setInbox(msgs: InboxMsg[]) {
    this.inbox = msgs;
    this.inboxFetched = true;
    this.inboxReads.inboxFrame(msgs);
    this.changed();
  }

  /** Unread messages: the count of the last Empire frame, less the reads
   *  it has not counted yet (audit P5). The head, the cap, the cell of the
   *  dock head and the tab icon all read this one figure. */
  get inboxUnread(): number {
    const ids = this.inboxReads.unreadIds(this.inbox);
    return this.inboxReads.unread(this.empire?.inboxUnread ?? 0, ids);
  }

  /** Does the reader see this message as read? A read the server has not
   *  answered yet counts (audit P5). */
  inboxIsRead(m: InboxMsg): boolean {
    return this.inboxReads.isRead(m.id, m.read);
  }

  /** The reader marked these messages read, or every message. The press
   *  sends the intent; this keeps the list and the counts true until the
   *  server answers. */
  markInboxRead(ids: readonly number[] | 'all') {
    if (ids === 'all') this.inboxReads.markAll(this.inbox.map((m) => m.id));
    else this.inboxReads.mark(ids);
    this.changed();
  }

  setMarket(m: Market) {
    this.market = m;
    this.changed();
  }

  /** The casino lobby (casino.md): the open tables, the quick queue and
   * the operator's CLOSED flag. It arrives on a cadence and replaces the
   * whole picture. The stamp anchors the queue card's two countdowns, the
   * wait and the fill, which the frame states as the seconds it had when
   * it left the server. */
  setCasinoLobby(l: CasinoLobby) {
    this.casino.lobby = l;
    this.casino.lobbyAtMs = performance.now();
    // The lobby is the AUTHORITY on where this reader sits (R988). A seat
    // the reader gave up, a table the house dissolved and a seat an
    // absent reader lost all end the same way: `your_table` reads 0 and
    // the last table frame is stale. A panel that kept it would draw a
    // dead table with no way back to the lobby. One socket is FIFO, so a
    // lobby that says 0 was built before any join this client has seen.
    if (l.yourTable === 0) this.dropCasinoTable();
    this.changed();
  }

  /** Forgets the table frame and its stamp: the seat is gone. */
  private dropCasinoTable() {
    this.casino.table = null;
    this.casino.tableAtMs = 0;
  }

  /**
   * Gives the seat up now, before a frame says so.
   *
   * The panel calls this on its own LEAVE press, because the server
   * answers a leave with a lobby frame on the lobby's own cadence and
   * with no table frame at all. The next lobby frame states the truth
   * either way.
   */
  clearCasinoTable() {
    if (!this.casino.table) return;
    this.dropCasinoTable();
    this.changed();
  }

  /**
   * One table frame. It stamps `tableAtMs` with a `performance.now()`
   * reading, because the frame states the real seconds the seat at turn
   * had left WHEN THE SERVER SENT IT. Every countdown the table draws
   * takes the difference against this stamp, so the bar drains between
   * frames and no interval runs.
   */
  setCasinoTable(t: CasinoTable) {
    // The fold reads the timeout fold off the last frame and this one
    // before it replaces the last (R2142).
    foldCasinoTable(this.casino, t, performance.now());
    this.changed();
  }

  /** One settled hand. The wheel lands and the reels stop from it, so the
   * frame is kept until the next hand replaces it. */
  setCasinoResult(r: CasinoResult) {
    // One counter a result, because `hand_id` alone cannot say that a
    // result is NEW: the slot machine holds no table and every pull may
    // carry one id. The counter rises with every frame of every game, so
    // a screen that took a reading before it sent an intent knows its own
    // answer when a higher reading arrives (R988). The fold also adds the
    // reader's move to the sum the visit net reads (R2115).
    foldCasinoResult(this.casino, r);
    // An aborted result is the table saying it is gone and that every
    // stake came back. The seat does not exist any more, so the slice
    // drops it and the panel shows the lobby.
    if (r.aborted && this.casino.table?.id === r.tableId) this.dropCasinoTable();
    this.changed();
  }

  setClan(info: ClanInfo) {
    this.clan = info.id === 0 ? null : info;
    this.changed();
  }

  setBoard(b: Scoreboard) {
    this.board = b;
    // The admin panel can retune the world live; the scoreboard carries
    // the current value so every stats readout follows without a refresh.
    if (b.timescale > 0) this.timescale = b.timescale;
    if (b.tacticalScale > 0) this.tacticalScale = b.tacticalScale;
    // The board already speaks world game-seconds, so it anchors directly —
    // including 0, which means the season is over and the settlement is
    // running: the banner has to reach zero rather than freeze one frame
    // short of it (R93).
    this.seasonLeftAtSec = b.seasonLeftSec;
    this.seasonAtMs = Date.now();
    // The truce anchor always follows the board: 0 means weapons are live
    // and the banner must not linger on a stale extrapolation.
    this.truceEndsMs = deadlineMs(b.truceLeftSec, this.timescale);
    // The settlement clock has its own message (R2093). A board before
    // stage 3 clears a clock that a roll left behind.
    if (b.quakeStage < 3) this.takeSettlement(undefined);
    // The world clock re-anchors on every board, after the two multipliers
    // above: a retune must not leave the reading running at the old rate.
    this.clockAtSec = b.clockSec;
    this.clockOriginSec = b.clockOriginSec;
    this.clockAtMs = Date.now();
    this.changed();
  }

  setSeasonReport(r: SeasonReport) {
    this.season = r;
    this.changed();
  }

  /** The season summary arrived (R1518). The card opens, unless the
   *  player already put away the card of this world and season. */
  setSeasonSummary(s: SeasonSummary) {
    this.summary = s;
    this.summaryAtMs = Date.now();
    this.summaryShown = this.summaryClosed !== this.summaryKey(s);
    this.changed();
  }

  /** The return briefing arrived (R1605). The overlay opens the INBOX on
   *  it; the store only holds it. */
  setBriefing(b: ReturnBriefing) {
    this.briefing = b;
    this.changed();
  }

  /** Opens or closes the season summary card. VIEW MAP closes it, and the
   *  STANDINGS tab opens it again. */
  showSummary(on: boolean) {
    const s = this.summary;
    const want = on && s !== null;
    if (this.summaryShown === want) return;
    this.summaryShown = want;
    this.summaryClosed = s !== null && !want ? this.summaryKey(s) : '';
    this.changed();
  }

  /** One world's one season, as the card's close remembers it. */
  private summaryKey(s: SeasonSummary): string {
    return `${this.hello?.worldId ?? 0}:${s.season}`;
  }

  setSieges(list: SiegeInfo[]) {
    this.sieges = list;
    this.changed();
  }

  setHeatmap(kind: HeatmapKind) {
    this.heatmap = kind;
    this.changed();
  }

  setProduction(p: ProductionState) {
    this.production = p;
    this.changed();
  }

  /** The inbox ledger arrived, answering a ledger query (R1851). */
  setInboxLedger(l: InboxLedgerState) {
    this.inboxLedger = l;
    this.changed();
  }

  /** The levels past the next of one building, answering a PreviewQuery
   *  (R1839). The build card reads it through `levelPreviews`. */
  setBuildingPreview(p: BuildingPreview) {
    this.levelPreviews.take(p);
    this.changed();
  }

  /** The decoded answer to a timelapse query, or null for a season with
   *  no frame yet (R1595). */
  setTimelapse(t: Timelapse | null) {
    this.timelapse = t;
    this.changed();
  }

  setWar(w: WarState) {
    this.war = w;
    this.warAtMs = Date.now();
    this.changed();
  }

  /** The contest on a site, if it is being fought over right now. */
  siegeOn(siteId: number): SiegeInfo | undefined {
    return this.sieges.find((s) => s.siteId === siteId);
  }

  select(sel: Selection) {
    this.selection = sel;
    this.changed();
  }

  /**
   * Takes a SiteOwner frame into the cached site (R475).
   *
   * The frame is the only news the client gets about a field between two
   * hellos: the banner, the tooltip's `≈ N units` and the drawn fill all
   * read the cache, so the cache must carry the stock and the capacity
   * the frame states. A frame for a site this client never heard of is
   * dropped — SiteAdded is the door a new site comes through.
   */
  setSiteOwner(
    siteId: number, faction: number, amount: number, capacity: number, effect = 0,
  ) {
    const site = this.sites.get(siteId);
    if (!site) return;
    site.faction = faction;
    // The mark the hold wears (R910). EVERY owner frame of a player base
    // states it, so the cache takes the value whole and never keeps an old
    // one.
    site.effect = effect;
    site.amount = amount;
    // A server before R475 sends no capacity. Keep the one we hold
    // rather than draw every field as full.
    if (capacity > 0) site.capacity = capacity;
  }

  /**
   * Forgets a site the world no longer has (SiteRemoved, R204).
   *
   * The seaquake's stage 2 kills every deposit for good. Until now the
   * server only sent `SiteOwner{amount: 0}`, which left a dimmed field on
   * the map for the rest of the season, promising a hauler somewhere to
   * go. A removed site leaves the cache entirely.
   *
   * The selection is not touched here. main.ts owns the one door every
   * selection change goes through, and it drops a selection that pointed
   * at the removed site — the map layer has to be told at the same time.
   */
  removeSite(siteId: number) {
    if (!this.sites.delete(siteId)) return;
    this.changed();
  }

  /**
   * One MaintenanceInfo frame off the wire (deployment.md §6.1). The hello
   * carries it as well, so a client that joins between the schedule and
   * the instant counts down from its first frame. A `startsAt` of 0 is the
   * cancellation, and it puts the drain line back to what it was.
   */
  setMaintenance(startsAt: number, endsAt: number, note: string) {
    this.maintenanceAt = startsAt;
    this.maintenanceEndsAt = endsAt;
    this.maintenanceNote = note;
    this.changed();
  }

  /** One SettlementClock off the wire (R2093). */
  setSettlement(c: SettlementClock) {
    this.takeSettlement(c);
    this.changed();
  }

  /** Stores a settlement clock, or clears it for none, without `changed()`. */
  private takeSettlement(c: SettlementClock | undefined) {
    this.settlementEndsMs = c ? Number(c.endsUnixMs) : 0;
    this.drainEndsMs = c ? Number(c.drainEndsUnixMs) : 0;
    this.settlementHeld = c?.held ?? false;
  }

  /** One QuakeInfo frame off the wire (R803). */
  setQuake(q: QuakeInfo) {
    this.quake = q;
    this.quakeAtMs = Date.now();
    this.changed();
  }

  /** World seconds to the next seaquake shock, counted down since the frame
   * that stated it (R803). A wall second is worth `timescale` world seconds,
   * the same rule as `seasonLeftSec`. 0 when no shock waits. */
  quakeNextSec(): number {
    if (!this.quake || this.quake.nextShockSec <= 0) return 0;
    return Math.max(0, this.quake.nextShockSec - this.worldSecSince(this.quakeAtMs));
  }

  /** Season game-seconds remaining (R93), extrapolated from the last wire
   * anchor on the world clock — the season is world time, so wall seconds
   * since the anchor are worth `timescale` game-seconds each. Presentation
   * only: the server rolls the season, the client just counts. */
  seasonLeftSec(): number {
    return Math.max(0, this.seasonLeftAtSec - this.worldSecSince(this.seasonAtMs));
  }

  /** WORLD seconds since the wall-clock ms `atMs`: a wall second is worth
   * `timescale` world seconds. Every anchored world countdown reads it. */
  private worldSecSince(atMs: number): number {
    return ((Date.now() - atMs) / 1000) * this.timescale;
  }

  /** WORLD seconds until a wall-clock deadline, extrapolated the way the
   * season is: a wall second is worth `timescale` world seconds. A deadline
   * of 0 is no countdown at all, and reads 0. */
  private leftSec(endsMs: number): number {
    return wallLeftSec(endsMs) * this.timescale;
  }

  /** WORLD seconds left on the timer of a live point row (R2735): the
   *  row's `left_sec` counted down since the frame that brought it. */
  poiLeftSec(seen: PoiLiveSeen): number {
    const left = seen.row.leftSec;
    return left > 0 ? Math.max(0, left - this.worldSecSince(seen.atMs)) : 0;
  }

  /** Founding-truce game-seconds remaining, extrapolated from the last
   * wire anchor on the world's own clock; 0 once weapons are live. */
  truceLeftSec(): number {
    return this.leftSec(this.truceEndsMs);
  }

  /** True in the card phase of the settlement (R2092): the drain has
   *  ended, and the season summary card stands until the roll. */
  settlementCard(): boolean {
    return this.drainEndsMs > 0 && Date.now() >= this.drainEndsMs;
  }

  /** WALL seconds left in the drain of the settlement (R2092), 0 outside
   *  it. The settlement runs on the wall clock (R2091). */
  drainLeftSec(): number {
    return wallLeftSec(this.drainEndsMs);
  }

  /** WALL seconds the settlement clock of the chrome shows (R2091, R2092):
   *  the drain while it runs, then the time to the end of the
   *  settlement. 0 outside the settlement. */
  settlementLeftSec(): number {
    if (this.settlementEndsMs === 0) return 0;
    return this.settlementCard() ? wallLeftSec(this.settlementEndsMs) : this.drainLeftSec();
  }

  /**
   * A duration in MAP seconds, read in WORLD seconds (R591).
   *
   * The wire states a duration in the clock the server paid it in
   * (server/CLAUDE.md "Three clocks"): a reload, a scan cooldown, an
   * anchor cycle and the intel shelf are map seconds. Every duration the
   * client SHOWS reads in world time, so a display site converts here and
   * nowhere else. World time is wall time on a production world, so the
   * figure a player reads is the wait the player feels.
   *
   * A tactical scale of 0 means "follow the world clock", so the two
   * clocks run together and the factor is 1.
   */
  mapToWorldSec(mapSec: number): number {
    if (!(this.tacticalScale > 0)) return mapSec;
    return (mapSec * this.timescale) / this.tacticalScale;
  }

  /** WORLD seconds until the base's automated point defence may fire
   * again (R86, R591), extrapolated like the truce; 0 = ready. The
   * cooldown is paid on the map clock, so the anchor holds a wall-clock
   * instant and a wall second is worth `timescale` world seconds. */
  pointDefenceReadySec(): number {
    return this.leftSec(this.pdReadyMs);
  }

  /** Whether the base's Torpedo Battery stands at the level the automated
   * point defence starts at (R1154). The gate is the server's figure from
   * the catalog rules, so the client mirrors no ladder. */
  pointDefenceArmed(): boolean {
    return this.level(Building.BUILDING_TORPEDOBANKS) >= this.rules.torpedobankPointDefenceLevel;
  }

  /** WORLD seconds until the next manual base tube reloads (R88, R591), 0
   * while one is already free — extrapolated like the point-defence
   * clock. */
  baseTubeReadySec(): number {
    return this.leftSec(this.tubeReadyMs);
  }

  /** World time of day in game-seconds, extrapolated on the world clock.
   * The day is 86400 game-seconds whatever the timescale is, so a wall
   * second is worth `timescale` of them and the reading wraps at midnight. */
  worldClockSec(): number {
    return secOfDay(this.clockAtSec + this.worldSecSince(this.clockAtMs));
  }

  /**
   * Game-seconds to a strategic object's next vulnerability window;
   * negative while the window is open.
   *
   * This mirrors `hub/objects.go` exactly. The client derives it rather
   * than reading `SiegeInfo.window_in_sec` because the Sieges frame is sent
   * on three edges only — join, a fleet commit, a window resolution — so
   * the wire figure is a snapshot that can sit still for hours. The wire
   * field stays as the cross-check.
   *
   * The wrap is the server's: a window is open for `rules.objWindowSec`
   * game-seconds, and once that span is spent the next window is
   * tomorrow's. The length is a pack figure, so it rides the runtime lane
   * in `Hello.catalog.rules` and is never a literal here (R355, R787).
   */
  windowInSec(hour: number): number {
    let inSec = hour * 3600 - this.worldClockSec();
    const span = this.rules.objWindowSec;
    if (inSec < -span) inSec += 86400;
    else if (inSec >= 86400 - span) inSec -= 86400; // yesterday's window is still open (R1455)
    return inSec;
  }

  /**
   * Whether a strategic object takes a pledge right now (R787).
   *
   * The window is the one time an object takes a pledge, attack or
   * defence alike, so this one reading gates the COMMIT button and the
   * right-click commit. It mirrors `sim.WindowOpen` statement for
   * statement, wrap and all, so the button and the server agree at the
   * instant the window opens and at the instant it shuts.
   */
  windowOpen(hour: number): boolean {
    const span = this.rules.objWindowSec;
    if (span <= 0) return false; // no hello yet: state no rule
    let since = this.worldClockSec() - hour * 3600;
    if (since < 0) since += 86400;
    return since < span;
  }

  /**
   * The world hour at which a window that opens at `hour` shuts (R787).
   *
   * The length is a pack figure on the runtime lane, so the chart reads
   * it here and holds no hour arithmetic of its own.
   */
  windowClosesHour(hour: number): number {
    return (hour + this.rules.objWindowSec / 3600) % 24;
  }

  /**
   * A queued job's live figures, extrapolated on the world clock from the
   * last Empire frame.
   *
   * `remainingMs` and `totalMs` are *work*, not time: the queue drains them
   * at a rate that the Works Yard level and the hull class decide, and
   * only the server holds that table. So `etaSec` is the honest countdown,
   * and the work figure is only good for the width of the progress bar.
   * The pair together gives the drain rate — work-seconds per world-second
   * — which is what steps the bar between frames.
   *
   * An inactive job holds no slot and drains nothing, so it counts nothing.
   */
  jobLive(j: Job): { etaSec: number; remainingMs: number } {
    if (!j.active || j.etaSec <= 0) return { etaSec: 0, remainingMs: j.remainingMs };
    const elapsed = this.worldSecSince(this.queueAtMs);
    const rate = (j.remainingMs / 1000) / j.etaSec;
    return {
      etaSec: Math.max(0, j.etaSec - elapsed),
      remainingMs: Math.max(0, j.remainingMs - rate * elapsed * 1000),
    };
  }

  /** World game-seconds until an active job lands, extrapolated; 0 for an
   * inactive job. */
  jobEtaSec(j: Job): number {
    return this.jobLive(j).etaSec;
  }

  /**
   * World game-seconds until a build slot admits a job appended now
   * (`Empire.queue_wait_sec`, R726), counted down from the Empire frame
   * the way `jobLive` counts a job: a draining queue sends no frame. 0
   * while a build slot is free. The start sheet of a dive reads it, because
   * the outfit job of a dive starts no sooner (R1899).
   */
  queueWaitLiveSec(): number {
    const wait = this.empire?.queueWaitSec ?? 0;
    if (wait <= 0) return 0;
    return Math.max(0, wait - this.worldSecSince(this.queueAtMs));
  }

  /**
   * World game-seconds from now until a dive started now surfaces (R1899):
   * the wait for a build slot, the outfit job at this empire's expedition
   * queue rate, then the dive itself. A dive's own duration is not queue
   * work, so no queue rate divides it (R688).
   */
  diveBackSec(durationSec: number): number {
    return this.queueWaitLiveSec()
      + this.buildSec(JobKind.JOB_KIND_EXPEDITION, this.rules.expeditionPrepSec) + durationSec;
  }

  /** Manual base tubes free to fire right now (R88). The server count is
   * exact when it arrives; once the anchored reload has run out at least
   * one tube is free again, whatever the last frame said. */
  baseTubesReady(): number {
    const n = this.empire?.baseTubesReady ?? 0;
    return this.baseTubeReadySec() > 0 ? n : Math.max(n, 1);
  }

  get catalog(): Catalog | null { return this.hello?.catalog ?? null; }

  /**
   * The loose rule figures the DOM's own sentences state (R355).
   *
   * They come off the server's APPLIED pack, so a world booted with
   * `-balance` moves the words on the screen. The client stores none of
   * them and computes nothing with them: a caller reads one, hands it to
   * a sentence, and the sentence writes it.
   *
   * `RULES_UNTIL_HELLO` stands in before the first Hello, so a panel that
   * draws while the socket opens shows a zero rather than a crash. Every
   * one of these sentences is an empty-state line, so the window is a
   * frame or two and nobody sees it.
   */
  get rules(): CatalogRules { return this.catalog?.rules ?? RULES_UNTIL_HELLO; }
  get faction(): number { return this.hello?.faction ?? 0; }
  get playerId(): number { return this.hello?.playerId ?? 0; }
  /**
   * The seat's own account id, for a rule that must know the reader's own
   * line or piece: the account where the hello names one, else the player
   * id of a legacy direct lane, else 0 before the hello.
   */
  get selfAccountId(): number { return this.hello?.accountId || this.hello?.playerId || 0; }
  get res(): Resources { return this.empire?.res ?? ZERO_RES; }

  building(id: Building): BuildingSpec | undefined {
    return this.catalog?.buildings.find((b) => b.id === id);
  }

  ship(id: ShipClass): ShipSpec | undefined {
    return this.catalog?.ships.find((s) => s.id === id);
  }

  tech(id: number): TechSpec | undefined {
    return this.catalog?.tech.find((t) => t.id === id);
  }

  torpedo(id: number): TorpedoSpec | undefined {
    return this.catalog?.torpedoes.find((t) => t.id === id);
  }

  /** One kind of sea object of the Depot (v2-the-deep.md §11.5, R2653):
   *  the hello carries the rows only while the world latched `sapper`. */
  seaObject(id: SeaObjectKind): SeaObjectSpec | undefined {
    return this.catalog?.seaObjects.find((k) => k.id === id);
  }

  /**
   * One module of the fit table, by family and tier (R700). The table is
   * the server's — its effect, its cost share, its gate and its draft
   * charge all ride the hello catalog — so the draft form reads it here
   * and mirrors nothing.
   */
  module(family: ModuleFamily, tier: number): ModuleSpec | undefined {
    return this.catalog?.modules.find((m) => m.family === family && m.tier === tier);
  }

  /**
   * Every round the hold holds a stock of and can fire (R86, R2783): the
   * base rounds in catalog order, then the ship rounds in the fixed order
   * (`holdRoundOrder`). A hold fires a ship round from the shared stock
   * since the torpedo refresh (R2779 rule 10).
   *
   * The hold card's round pick, the `d` key, the `n` key and the aim ring
   * all read this. They did not: the key tested the FIRST `from_base`
   * torpedo alone, so a hold that stocked interceptors and no heavy
   * answered the key with nothing, while the panel's button worked. One
   * reader is what stops them drifting again (R469).
   */
  stockedHoldRounds(): TorpedoSpec[] {
    const out: TorpedoSpec[] = [];
    for (const k of holdRoundOrder(this.catalog?.torpedoes ?? [])) {
      const spec = this.torpedo(k);
      if (spec && (this.empire?.torpedoes.find((h) => h.kind === k)?.count ?? 0) > 0) out.push(spec);
    }
    return out;
  }

  level(id: Building): number {
    return this.empire?.buildings.find((b) => b.building === id)?.level ?? 0;
  }

  /** How many hulls of a class sit in the pens. A row is one fleet now, so
   * this counts rows rather than reading a pool's tally. */
  stock(cls: ShipClass): number {
    return this.empire?.stock.filter((s) => s.cls === cls).length ?? 0;
  }

  /** The penned fleets, oldest first — one row per hull. */
  penned() {
    return this.empire?.stock ?? [];
  }

  /** The hull in the pens with this fleet id, or undefined. */
  pennedHull(fleetId: number) {
    return this.penned().find((p) => p.fleetId === fleetId);
  }

  /** Is a repair of this hull in the build queue? The repair job names
   *  the fleet id as its target (`game.Player.RepairFleet`), and a hull
   *  under repair does not sail (R1883, `ui/deployask.ts`). */
  underRepair(fleetId: number): boolean {
    return fleetId > 0 && this.queue.some((j) => j.kind === JobKind.JOB_KIND_REPAIR && j.target === fleetId);
  }

  /** The print installed in a hull class's slot, or 0 for the default. */
  installed(cls: ShipClass): number {
    return this.empire?.installed[cls] ?? 0;
  }

  /**
   * The rate this empire drains a job of `kind` at, in work-seconds per
   * world second (numbers.md §1, R688). The wire array is dense and
   * indexed by the JobKind enum value, exactly as `installed` is indexed
   * by ShipClass.
   *
   * The answer is 1 when the wire says nothing, so a panel that draws
   * before the first Empire frame states the list time and no menu
   * breaks. It is 1 as well when the figure is not finite: the research
   * speed is `1 / (1 - cut x lab)` and it diverges at a high enough Lab,
   * and `-balance` moves both the dial and the cap at boot. Without the
   * guard the panel prints `at Infinityx`.
   */
  queueRate(kind: JobKind): number {
    const r = this.empire?.queueRate[kind] ?? 1;
    return r > 0 && Number.isFinite(r) ? r : 1;
  }

  /**
   * World seconds a job of `kind` takes this empire, from the catalog's
   * work-seconds at 1x — the list time (numbers.md §1, R688).
   *
   * The ceiling mirrors `hub/convert.go`, which rounds `eta_sec` up. A
   * row would otherwise read `15m 0s` while the queue strip reads
   * `15m 1s` for the same job.
   */
  buildSec(kind: JobKind, workSec: number): number {
    return Math.ceil(workSec / this.queueRate(kind));
  }

  /**
   * The Command Centre level cap this empire holds: the cap of its highest
   * Foundations node, else the default (the server's `BaseLevelCap`, which
   * the catalog states for the row to read).
   */
  baseCap(): number {
    let cap = this.rules.baseCapDefault;
    for (const t of this.catalog?.tech ?? []) {
      if (t.baseCap > cap && this.hasTech(t.id)) cap = t.baseCap;
    }
    return cap;
  }

  hasTech(id: number): boolean {
    return this.empire?.tech.includes(id) ?? false;
  }

  /**
   * Does this empire hold the node with that branch code (R721)? The code
   * is the stable name of a node — the id behind it is a wire value the
   * server derives — so a panel that must know about one node asks for
   * "A8" and mirrors no table. An empire with no catalog yet holds
   * nothing, and the server refuses the order anyway.
   */
  hasTechCode(code: string): boolean {
    const n = this.catalog?.tech.find((t) => t.code === code);
    return !!n && this.hasTech(n.id);
  }

  /** Does this seat hold one of the server's feature keys? The hello lists
   * what the seat carries; a client that has not had a hello yet, or a
   * server that never sent the key, holds nothing. */
  hasFeature(key: string): boolean {
    return this.hello?.features.includes(key) ?? false;
  }

  /** Is one feature flag on for this world (v2-the-deep.md §17.3, R1574)?
   * The hello carries the set the server latched when it founded the map.
   * A client with no hello yet holds every flag off. This is not an
   * entitlement key: `hasFeature` reads those. The server is the gate,
   * and this check only hides the controls of a part that is off. */
  hasFeatureFlag(flag: FeatureFlag): boolean {
    // A hello built in part (a test fixture, a replay viewer's stand-in)
    // may carry no list, and a page must read that as no flag.
    return this.hello?.featureFlags?.includes(flag) ?? false;
  }

  /** Does one faction hold commanders on this world (R3095)? A custom
   *  world may switch factions off, and `Hello.factions` then lists the
   *  enabled set; an empty list, or no hello yet, means all five. Every
   *  faction list of the game UI hides a faction this answers false for. */
  factionOn(id: number): boolean {
    const list = this.hello?.factions ?? [];
    return list.length === 0 || list.includes(id);
  }

  /** The depth band of each cell from the hello (v2 §3.1, R2609, R2616),
   *  or null while the flag `depth` is off. A canvas piece reads it and no
   *  flag: an empty field draws nothing. */
  get depthBands(): Uint8Array | null {
    const bytes = this.hello?.depth;
    if (bytes !== this.depthBytes) {
      this.depthBytes = bytes;
      // An own copy, because a survey pass writes the bands of its new
      // cells into it (R2626), and the hello stays what the wire sent.
      const f = bandField(bytes);
      this.depthView = f ? f.slice() : null;
    }
    return this.depthView;
  }
  private depthBytes: Uint8Array | undefined = undefined;
  private depthView: Uint8Array | null = null;

  /** The current field of the hello as signed pairs (v2 §5.1, R2609), or
   *  null while the flag `currents` is off. One own copy for each hello,
   *  which a survey pass grows (R2626). */
  get currents(): Int8Array | null {
    const bytes = this.hello?.currents;
    if (bytes !== this.currentBytes) {
      this.currentBytes = bytes;
      this.currentView = currentField(bytes ? bytes.slice() : bytes);
    }
    return this.currentView;
  }
  private currentBytes: Uint8Array | undefined = undefined;
  private currentView: Int8Array | null = null;

  /**
   * Takes the survey of one State frame (v2 §4.1, §14.9, R2626): the
   * bitset, the known points, and the bands and the currents of the new
   * cells, which grow the fields of the hello. Answers what changed, or
   * null. The scene calls it once a frame.
   */
  applySurvey(s: State): SurveyDelta | null {
    this.survey.live.apply(s.poiLive, Date.now());
    const d = this.survey.applyState(s);
    // A full frame carries the bands of the cells the picture lacks too
    // (R2633), so both kinds merge `surveyed`. A full frame also takes
    // away the band and the current of a cell its bitset does not hold
    // (R2825): the frame after TOGGLE VEIL goes off.
    let moved = !!d && mergeSurveyed(s.surveyed ?? [], s.surveyedBands, s.surveyedCurrents,
      this.depthBands, this.currents);
    const bits = this.survey.bits;
    if (d?.full && bits && clearUnsurveyed(bits, this.depthBands, this.currents)) moved = true;
    // A delta names the cells it wrote, so a cache of the fields updates
    // those cells alone (R2897); a full frame builds every cache whole.
    if (moved) fieldChanged(d?.full ? undefined : s.surveyed);
    return d;
  }

  /** Takes the cargo sightings of one State frame (v2 §9.6, R2843): a
   *  full frame replaces the list, a delta writes its rows and drops the
   *  gone ids. Answers whether a row moved. A wreck that gains or loses
   *  its sighting, or whose row turns recovered, moves `cargoSightingsRev`
   *  (R2847, R2850). */
  applyCargoSightings(s: State): boolean {
    const rows = s.cargoSightings ?? [];
    const gone = s.cargoSightingsGone ?? [];
    if (!s.full && rows.length === 0 && gone.length === 0) return false;
    const old = this.cargoSightings;
    const had = old.size;
    if (s.full) this.cargoSightings = new Map();
    const now = this.cargoSightings;
    let keys = false;
    for (const r of rows) {
      const was = now.get(r.fleet);
      if (!s.full && (!was || !was.recovered !== !r.recovered)) keys = true;
      now.set(r.fleet, r);
    }
    for (const f of gone) if (now.delete(f) && !s.full) keys = true;
    if (s.full) {
      keys = now.size !== had;
      if (!keys) {
        for (const [f, r] of now) {
          const was = old.get(f);
          if (!was || !was.recovered !== !r.recovered) { keys = true; break; }
        }
      }
    }
    if (keys) this.cargoSightingsRev++;
    return rows.length > 0 || gone.length > 0 || had > 0;
  }

  /** Takes the admin cache list of one State frame (R2825): presence
   *  replaces the list, absence keeps it. Answers whether it changed. */
  applyAdminCaches(s: State): boolean {
    if (!s.adminCaches) return false;
    this.adminCaches = s.adminCaches.rows;
    return true;
  }

  /** World seconds into the tide cycle of `CatalogRules` (v2 §5.3). */
  tideElapsedSec(): number {
    return tideElapsedSec(this.worldClockSec(), this.clockOriginSec, this.rules.tidePeriodSec,
      this.seasonElapsedSec());
  }

  /** The factor of the tide now, from the table of the hello (R2616). */
  tideFactor(): number {
    return tideFactorAt(this.tideElapsedSec(), this.rules.tidePeriodSec, this.rules.tideSteps);
  }

  /** The tide clock now, or null before the hello carries a table. */
  tideNow(): TideClock | null {
    return tideClock(this.tideElapsedSec(), this.rules.tidePeriodSec, this.rules.tideSteps);
  }

  captain(id: number): Captain | undefined {
    return this.empire?.captains.find((c) => c.id === id);
  }

  /** The fit of one own fleet at sea (R1501), empty when the empire frame
   *  names none: a fleet with no module, or a hull that is not ours. */
  fleetFit(id: number): Module[] {
    return this.empire?.fleetFits.find((x) => x.fleetId === id)?.fit ?? [];
  }

  /** Roster members (cadets are offers, not crew). */
  get roster(): Captain[] { return this.empire?.captains.filter((c) => !c.cadet) ?? []; }
  get cadets(): Captain[] { return this.empire?.captains.filter((c) => c.cadet) ?? []; }

  get queue(): Job[] { return this.empire?.queue ?? []; }

  canAfford(cost: Resources | undefined): boolean {
    if (!cost) return false;
    const r = this.res;
    return r.alloy >= cost.alloy && r.biomass >= cost.biomass && r.volatiles >= cost.volatiles
      && r.isotopes >= cost.isotopes && r.credits >= cost.credits;
  }
}

// --- formatting helpers shared by every panel ---

/** The five commodities in the order of every cost and readout. */
export const RES_ORDER: readonly (keyof Resources)[] = ['alloy', 'biomass', 'volatiles', 'isotopes', 'credits'];

/**
 * A cost, each commodity in its own colour (ui-map-interface.md §2) and
 * with its drawn icon (R1768). Every price in the UI goes through here, so
 * a glance at a row says *which* resource is short, not just that
 * something is.
 */
export function costText(c: Resources | undefined, have?: Resources): string {
  if (!c) return '—';
  const parts: string[] = [];
  for (const key of RES_ORDER) {
    const v = c[key];
    // With the stores at hand, a part the stores cannot pay wears
    // `short`: the press of a build row inks it in the bad ink (R1813).
    const short = have !== undefined && have[key] < v ? ' short' : '';
    if (v) parts.push(`<span class="r r-${key}${short}">${resIcon(key)}${num(v)}</span>`);
  }
  return parts.length ? parts.join(' ') : S.cost_free;
}

/**
 * A price for a batch of n. The BUILD menu offers the ship-fired rounds
 * four at a time, so the row must state the price and the clock of the
 * order it actually places (R698). Nothing here is a rule: it is the
 * catalog's own cost, multiplied by the count the button sends.
 */
export function mulRes(c: Resources | undefined, n: number): Resources | undefined {
  if (!c) return undefined;
  return {
    alloy: c.alloy * n, biomass: c.biomass * n, volatiles: c.volatiles * n,
    isotopes: c.isotopes * n, credits: c.credits * n,
  };
}

export function num(v: number): string {
  if (v >= 1_000_000) return S.num_millions({ n: (v / 1_000_000).toFixed(1) });
  if (v >= 10_000) return S.num_thousands({ n: Math.round(v / 1000) });
  return String(Math.round(v));
}

/** Formats a duration of *game* seconds as a human span: `spanText` of
 *  `locale/span.ts` (R2741), a wait under a minute rounded up, and a dash
 *  for no wait. */
export function dur(sec: number): string {
  if (!(sec > 0)) return '—';
  return spanText(sec < 60 ? Math.ceil(sec) : sec);
}

// The mission table (v5, R40, R79, R114): the index is the wire's own
// enum value, so slot 7 — the dead capture cycle — keeps its place and a
// flip back is a code change and not a protocol change.
// The array type is widened from the generated tuple on purpose: a wire
// enum can carry UNRECOGNIZED (-1), and a tuple refuses a negative index.
export const MISSION_NAMES: readonly string[] = S.mission_names;

/**
 * Resource names by the wire's deposit ids (1 alloy, 2 biomass, 3
 * volatiles, 4 isotopes). Slot 0 is "no resource".
 *
 * DERIVED from the identity table and not a second list of the same
 * words (R353): `data/identity/identity.toml` names every resource, and
 * a deposit word is that name in lower case. Credits are earned and
 * never mined, so the table stops at the isotopes and the two call sites
 * name credits themselves.
 */
export const RESOURCE_NAMES: readonly string[] = [
  '—',
  ...RESOURCES.filter((r) => r.key !== 'credits').map((r) => r.name.toLowerCase()),
];
